import { useState, useMemo, useCallback } from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import {
  Card,
  DropdownMenu,
  IconButton,
  SwatchGroup,
  Text,
  PencilIcon,
  AllChannelsIcon,
  TrashIcon,
  ChartColumnIcon,
  EllipsisVerticalIcon,
  toast,
  UnstyledButton,
  Tag as PopcornTag,
} from '@buffer-mono/popcorn'

import { colors } from '~publish/components/TagEditDialog/colors'
import { useTagReportingLink } from '~publish/hooks/useTagReportingLink'
import { useTags } from '~publish/hooks/useTags'
import type { Tag } from '~publish/hooks/useTags'
import { allChannels } from '~publish/legacy/routes'

import { DeleteConfirmationDialog } from './DeleteConfirmationDialog'

import styles from './TagItem.module.css'

interface TagItemProps {
  tag: Tag
  canManageTags: boolean
  onEditTag?: (tag: Tag) => void
}

const sortedColors = colors.toSorted(({ name: a }, { name: b }) => {
  const isASpecial = a === 'Black' || a === 'Gray'
  const isBSpecial = b === 'Black' || b === 'Gray'

  // Both Black and Gray should go to the end
  if (isASpecial && isBSpecial) {
    // Black before Gray
    if (a === 'Black') return -1
    if (b === 'Black') return 1
    return 0
  }

  // If only a is special, move it to the end
  if (isASpecial) return 1

  // If only b is special, move it to the end
  if (isBSpecial) return -1

  // Normal alphabetical sort for all other colors
  return a.localeCompare(b)
})

export const TagItem = ({
  tag,
  canManageTags,
  onEditTag,
}: TagItemProps): JSX.Element => {
  const [openDropdown, setOpenDropdown] = useState(false)
  const { updateTag, deleteTag } = useTags()
  const getReportingLinkProps = useTagReportingLink()

  const postViewUrl = useMemo(() => {
    return allChannels.getRoute({ tagIds: [tag.id] })
  }, [tag.id])

  // Only update color if it has actually changed
  const handleColorChange = useCallback(
    async (newColor: string): Promise<void> => {
      if (newColor === tag.color) return

      try {
        await updateTag({ id: tag.id, name: tag.name, color: newColor })
      } catch {
        toast.error('Failed to update tag color. Please try again later.')
      }
    },
    [tag.id, tag.name, tag.color, updateTag],
  )

  const handleDelete = useCallback(async (): Promise<void> => {
    try {
      await deleteTag(tag.id)
      toast.success(`Tag "${tag.name}" deleted successfully.`)
    } catch {
      toast.error('Failed to delete tag. Please try again later.')
    }
  }, [tag.id, tag.name, deleteTag])

  const handleColorInputChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      void handleColorChange(evt.target.value)
    },
    [handleColorChange],
  )

  return (
    <Card className={styles.tagItem}>
      {/* Color Picker */}
      <div className={styles.colorCell}>
        {canManageTags ? (
          <DropdownMenu>
            <DropdownMenu.Trigger>
              <UnstyledButton
                className={styles.colorButton}
                aria-label={`Change color for ${tag.name}`}
              >
                <PopcornTag
                  className={styles.colorDot}
                  color={tag.color}
                  aria-label={`Tag color: ${tag.color}`}
                >
                  {' '}
                </PopcornTag>
              </UnstyledButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              <label
                htmlFor={`colorPicker-${tag.id}`}
                className={styles.colorPickerLabel}
              >
                Select Color
              </label>
              <SwatchGroup
                id={`colorPicker-${tag.id}`}
                onChange={handleColorInputChange}
                className={styles.swatchGroup}
                value={tag.color}
              >
                {sortedColors.map(({ color, name }) => (
                  <SwatchGroup.Swatch
                    key={color}
                    color={color}
                    name={name}
                    aria-label={`color ${color}`}
                  />
                ))}
              </SwatchGroup>
            </DropdownMenu.Content>
          </DropdownMenu>
        ) : (
          <PopcornTag
            className={styles.colorDot}
            color={tag.color}
            aria-label={`Tag color: ${tag.color}`}
          >
            {' '}
          </PopcornTag>
        )}
      </div>

      {/* Tag Name */}
      <div className={clsx(styles.nameCell, styles.grow)}>
        <Text size="md" weight="bold" translate="no">
          {tag.name}
        </Text>
      </div>

      {/* Actions */}
      <div className={clsx(styles.actionsCell)}>
        {canManageTags && (
          <IconButton
            variant="tertiary"
            label={`Edit ${tag.name}`}
            onClick={() => onEditTag?.(tag)}
          >
            <PencilIcon />
          </IconButton>
        )}

        {canManageTags && (
          <DeleteConfirmationDialog tag={tag} onDeleteConfirm={handleDelete}>
            <IconButton variant="critical-subtle" label={`Delete ${tag.name}`}>
              <TrashIcon />
            </IconButton>
          </DeleteConfirmationDialog>
        )}

        <DropdownMenu open={openDropdown} onOpenChange={setOpenDropdown}>
          <DropdownMenu.Trigger asChild>
            <IconButton
              variant="tertiary"
              label={`More actions for ${tag.name}`}
            >
              <EllipsisVerticalIcon />
            </IconButton>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content>
            <DropdownMenu.Item asChild>
              <a href={postViewUrl} target="_blank" rel="noopener noreferrer">
                <AllChannelsIcon />
                View posts
              </a>
            </DropdownMenu.Item>
            <DropdownMenu.Item asChild>
              <a rel="noopener noreferrer" {...getReportingLinkProps(tag.id)}>
                <ChartColumnIcon />
                Open Reporting
              </a>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu>
      </div>
    </Card>
  )
}
